/* ==========================================================================
   Founder Image - Asian American Forward Website
   ========================================================================== */

import { preventModalScroll, allowModalScroll } from './utils/scroll-prevention.js';

let founderImageContainer = null;

export function initializeFounderImage() {
  const founderProfile = document.querySelector('.founder-profile');
  if (!founderProfile) return;

  founderImageContainer = document.querySelector('.founder-img-container');
  
  if (!founderImageContainer) {
    return;
  }
  
  // Set up click handler for expansion
  founderImageContainer.addEventListener('click', () => toggleImageExpansion());
  founderImageContainer.setAttribute('tabindex', '0');
  founderImageContainer.setAttribute('role', 'button');
  founderImageContainer.setAttribute('aria-label', 'View founder photo in fullscreen');
  
  // Add keyboard support
  founderImageContainer.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleImageExpansion();
    }
  });
  
  // Set up 3D hover effects (exactly like hero cards)
  setup3DMouseEffect(founderImageContainer);
}

/* ==========================================================================
   3D Mouse Effect (Exactly like Hero Cards)
   ========================================================================== */

function setup3DMouseEffect(container) {
  let isHovering = false;
  
  container.addEventListener('mouseenter', (e) => {
    isHovering = true;
    container.style.transition = 'all 0.1s ease-out'; // Smooth transition for hover
  });
  
  container.addEventListener('mouseleave', (e) => {
    isHovering = false;
    container.style.transition = 'all 0.4s ease-out'; // Smooth transition back
    
    // Reset transforms using CSS custom properties (exactly like hero cards)
    resetFounderTransform(container);
  });
  
  container.addEventListener('mousemove', (e) => {
    if (!isHovering) return;
    
    const rect = container.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    // Calculate mouse position relative to container center (-1 to 1)
    const mouseX = (e.clientX - centerX) / (rect.width / 2);
    const mouseY = (e.clientY - centerY) / (rect.height / 2);
    
    // Calculate tilt intensity based on card size (responsive tilt)
    // Base reference: 300px width = 8 degrees, scales inversely
    const baseWidth = 300;
    const baseTilt = 5;
    const scaledTilt = (baseWidth / rect.width) * baseTilt;
    const maxTilt = Math.max(2, Math.min(5, scaledTilt)); // Clamp between 2-8 degrees
    
    const tiltX = mouseY * maxTilt; // Vertical mouse movement tilts around X axis
    const tiltY = -mouseX * maxTilt; // Horizontal mouse movement tilts around Y axis
    
    // Apply 3D tilt using CSS custom properties (exactly like hero cards)
    container.style.setProperty('--card-tilt-x', `${tiltX}deg`);
    container.style.setProperty('--card-tilt-y', `${tiltY}deg`);
    container.style.setProperty('--card-translate-z', '10px'); // Lift the container
    container.style.setProperty('--card-z-index', '50'); // Ensure it stays on top
  });
}

function resetFounderTransform(container) {
  // Simply remove the hover transforms - CSS will handle the base positioning (exactly like hero cards)
  container.style.removeProperty('--card-tilt-x');
  container.style.removeProperty('--card-tilt-y');
  container.style.removeProperty('--card-translate-z');
  container.style.removeProperty('--card-z-index');
}

/* ==========================================================================
   Expand/Contract Functionality (Same as Hero Cards)
   ========================================================================== */

function toggleImageExpansion() {
  const modal = document.getElementById('card-modal');
  const modalImage = document.getElementById('modal-image');
  const founderImg = founderImageContainer.querySelector('.founder-img');
  
  if (!modal || !modalImage || !founderImg) {
    return;
  }
  
  // Check if modal is already open
  const isModalOpen = modal.style.display === 'flex';
  
  if (!isModalOpen) {
    expandImage();
  }
  // Note: We don't need to handle closing here since the modal handles it
}

function expandImage() {
  const modal = document.getElementById('card-modal');
  const modalImage = document.getElementById('modal-image');
  const founderImg = founderImageContainer.querySelector('.founder-img');
  
  if (!modal || !modalImage || !founderImg) return;
  
  // Ensure this is only called for founder images, not other cards
  if (!founderImageContainer.classList.contains('founder-img-container')) {
    return;
  }
  
  // Set modal image source
  modalImage.src = founderImg.src;
  modalImage.alt = founderImg.alt;
  
  // Prevent scrolling
  preventModalScroll();
  
  // Show modal (same as hero cards)
  modal.style.display = 'flex';
  modal.setAttribute('aria-hidden', 'false');
  modalImage.focus({ preventScroll: true });
  
  // Set up close handler (same as hero cards)
  const closeModal = () => {
    modal.style.display = 'none';
    modal.setAttribute('aria-hidden', 'true');
    
    // Re-enable scrolling
    allowModalScroll();
    
    founderImageContainer.focus({ preventScroll: true }); // Return focus to image container
    
    // Remove event listeners
    modal.removeEventListener('click', closeModal);
    if (closeButton) {
      closeButton.removeEventListener('click', closeModal);
    }
    document.removeEventListener('keydown', handleKeydown);
  };
  
    const handleKeydown = (e) => {
    if (e.key === 'Escape') {
      closeModal();
      document.removeEventListener('keydown', handleKeydown);
    }
  };

  // Get modal elements
  const closeButton = modal.querySelector('.card-modal-close');

  // Add event listeners - close modal when clicking anywhere on the modal (including the image)
  modal.addEventListener('click', closeModal);
  if (closeButton) {
    closeButton.addEventListener('click', closeModal);
  }
  document.addEventListener('keydown', handleKeydown);
}

/* ==========================================================================
   Public API
   ========================================================================== */

export function getFounderImageContainer() {
  return founderImageContainer;
} 